import { useMemo, useState } from "react";
import { useParams } from "react-router";
import {
  Button,
  Card,
  Descriptions,
  Flex,
  Grid,
  Typography,
  message,
} from "antd";
import { format } from "date-fns";
import { trainingCatalogService } from "../../../services/training-catalog.service";
import StatusTag from "../../../components/StatusTag";
import { useGetDetailApi } from "../../../hooks/useApi";
import { fileService } from "../../../services/file.service";
import DocumentPreviewModal from "../../../components/DocumentPreviewModal";

export default function ParticipantsDetail() {
  const { catalogId, participantId } = useParams();
  const breakpoint = Grid.useBreakpoint();

  const [messageApi, contextHolder] = message.useMessage();
  const [previewFileData, setPreviewFileData] = useState(null);

  const extractUUID = (filePath) => {
    if (!filePath) return null;
    const match = filePath.match(/[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}/);
    return match ? match[0] : null;
  };

  const handlePreview = async (filePath, isPdf) => {
    const uuid = extractUUID(filePath);
    if (!uuid) {
      messageApi.error("UUID file tidak ditemukan");
      return;
    }

    try {
      messageApi.loading({ content: "Memuat dokumen...", key: "preview" });
      const blob = await fileService.getFile(uuid);
      const url = URL.createObjectURL(blob);
      setPreviewFileData({ url, isPdf });
      messageApi.success({ content: "Dokumen berhasil dimuat", key: "preview", duration: 2 });
    } catch (error) {
      console.error("Preview error:", error);
      messageApi.error({ content: "Gagal memuat dokumen", key: "preview", duration: 2 });
    }
  };

  const handleDownloadFile = async (filePath, fileName) => {
    const uuid = extractUUID(filePath);
    if (!uuid) {
      messageApi.error("UUID file tidak ditemukan");
      return;
    }

    try {
      messageApi.loading({ content: "Mengunduh dokumen...", key: "download" });
      const blob = await fileService.getFile(uuid);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      messageApi.success({ content: "Dokumen berhasil diunduh", key: "download", duration: 2 });
    } catch (error) {
      console.error("Download error:", error);
      messageApi.error({ content: "Gagal mengunduh dokumen", key: "download", duration: 2 });
    }
  };

  const labelStyle = {
    width: breakpoint.lg ? 260 : "100%",
    minWidth: 260,
  };

  const contentStyle = {
    width: breakpoint.lg ? "calc(100% - 260px)" : "100%",
  };

  const descriptionTitle = (title) => (
    <Typography.Title
      level={3}
      style={{
        marginBottom: 0,
        fontWeight: "bold",
        fontSize: 18,
      }}
    >
      {title}
    </Typography.Title>
  );

  const { data } = useGetDetailApi({
    id: participantId,
    serviceFn: trainingCatalogService.fetchByParticipantId,
    queryKey: ["participants"],
  });

  const statusItems = useMemo(() => {
    if (!data) return [];

    const items = [
      {
        label: "Status",
        children: <StatusTag status={data?.status?.registration} />,
        span: 2,
      },
    ];

    if (data?.status?.registration === "Ditolak") {
      items.push({
        label: "Alasan",
        children: data?.reason ?? "-",
        span: 2,
      });
    }

    if (data?.status?.registration === "Diterima") {
      items.push({
        label: "Sertifikat",
        children: data?.status?.certificate ? (
          <Flex gap={8} align="center">
            <FileTextOutlined />
            <Typography.Text style={{ marginRight: "auto" }}>
              {data?.status?.certificate?.fileName}
            </Typography.Text>
            <Button
              variant="outlined"
              color="default"
              size="small"
              onClick={() =>
                handleDownloadFile(data?.status?.certificate?.filePath, data?.status?.certificate?.fileName)
              }
            >
              Unduh
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() =>
                handlePreview(data?.status?.certificate?.filePath, data?.status?.certificate?.fileName?.toLowerCase().includes(".pdf"))
              }
            >
              Lihat
            </Button>
          </Flex>
        ) : (
          "-"
        ),
        span: 2,
      });
    }

    return items;
  }, [data]);

  const personalDataItems = useMemo(() => {
    if (!data) return [];

    return [
      {
        label: "NIK",
        children: data?.nik || "-",
      },
      {
        label: "Nama Depan",
        children: data?.pmi?.firstName || "-",
      },
      {
        label: "Nama Belakang",
        children: data?.pmi?.lastName || "-",
      },
      {
        label: "Jenis Kelamin",
        children: data?.pmi?.gender || "-",
      },
      {
        label: "Tempat Lahir",
        children: data?.pmi?.birthPlace || "-",
      },
      {
        label: "Tanggal Lahir",
        children: data?.pmi?.birthDate
          ? format(new Date(data?.pmi?.birthDate), "dd/MM/yyyy")
          : "-",
      },
      {
        label: "Email",
        children: data?.pmi?.email || "-",
      },
      {
        label: "Telepon",
        children: data?.pmi?.phone || "-",
      },
    ];
  }, [data]);

  const regularRequirementDocumentItems =
    data?.files
      ?.map((file, index) => [
        {
          label: file.documentType,
          children: file.fileName ? (
            <Flex gap={8} align="center">
              {/* <FileTextOutlined /> */}
              <Typography.Text style={{ marginRight: "auto" }}>
                {file?.fileName}
              </Typography.Text>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => handleDownloadFile(file?.filePath, file?.fileName)}
              >
                Unduh
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => handlePreview(file?.filePath, file?.fileName?.toLowerCase().includes(".pdf"))}
              >
                Lihat
              </Button>
            </Flex>
          ) : (
            "-"
          ),
          span: 2,
        },
      ])
      .flat() || [];

  const interestDataItems = useMemo(() => {
    if (!data) return [];

    return [
      {
        label: "Negara Peminatan",
        children: data?.training?.requestedCountry || "-",
        span: 2,
      },
      {
        label: "Sektor Pekerjaan",
        children: data?.training?.sectoral || "-",
      },
      {
        label: "Jabatan",
        children: data?.training?.position || "-",
      },
    ];
  }, [data]);

  return (
    <>
      {contextHolder}
      <Card title={descriptionTitle("Detail Peserta Pelatihan")}>
        <Descriptions
          style={{ marginBottom: 20 }}
          column={breakpoint.lg ? 2 : 1}
          bordered
          size="small"
          styles={{
            label: labelStyle,
            content: contentStyle,
          }}
          title={descriptionTitle("Status")}
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={statusItems}
        />

        <Descriptions
          style={{ marginBottom: 20 }}
          column={breakpoint.lg ? 2 : 1}
          bordered
          size="small"
          styles={{
            label: labelStyle,
            content: contentStyle,
          }}
          title={descriptionTitle("Data Diri")}
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={personalDataItems}
        />

        <Descriptions
          style={{ marginBottom: 20 }}
          column={breakpoint.lg ? 2 : 1}
          bordered
          size="small"
          styles={{
            label: labelStyle,
            content: contentStyle,
          }}
          title={descriptionTitle("Data Peminatan")}
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={interestDataItems}
        />

        <Descriptions
          style={{ marginBottom: 20 }}
          column={breakpoint.lg ? 2 : 1}
          bordered
          size="small"
          styles={{
            label: labelStyle,
            content: contentStyle,
          }}
          title={descriptionTitle("Dokumen Persyaratan Reguler")}
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={regularRequirementDocumentItems}
        />
      </Card>

      <DocumentPreviewModal
        open={!!previewFileData}
        src={previewFileData?.url || ""}
        isPdf={previewFileData?.isPdf}
        title={previewFileData?.isPdf ? "Preview Dokumen" : "Preview Gambar Pelatihan"}
        onClose={() => {
          if (previewFileData?.url) URL.revokeObjectURL(previewFileData.url);
          setPreviewFileData(null);
        }}
      />
    </>
  );
}
